import { Signal } from '@angular/core';
import { AuditLog as AuditLogEntry } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';
import { encodeDateParam } from '@tourfold/libraries/shared-util';
import { AuditLogFilterValues } from './shared/audit-log-filters/audit-log-filters';

const MAX_VISIBLE_AUDIT_LOGS = 25;

export interface AuditLogAiPageContextSignals {
  readonly auditLogs: Signal<AuditLogEntry[]>;
  readonly filterValues: Signal<AuditLogFilterValues>;
  readonly sortField: Signal<string | null>;
  readonly sortOrder: Signal<number>;
}

/**
 * Registers AI assistant page context for the audit-log settings page.
 *
 * - `visible_rows` carries the first 25 entries with a synthetic
 *   `eventType (scopeType)` label so the model can identify rows without
 *   raw description content.
 * - `filters` reflects the date range, scope/actor filters, and sort.
 *
 * No `entity` — audit log is a list view; rows are referenced by id only.
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideAuditLogAiPageContext(state: AuditLogAiPageContextSignals): void {
  providePageContext(() => {
    const filters = state.filterValues();
    const sortField = state.sortField();
    const sortOrder = state.sortOrder();

    return {
      visible_rows: state
        .auditLogs()
        .slice(0, MAX_VISIBLE_AUDIT_LOGS)
        .map((entry) => ({
          type: 'audit_log',
          id: entry.id,
          label: `${entry.eventType} (${entry.scopeType})`,
        })),
      filters: {
        ...(filters.fromDate && { from_date: encodeDateParam(filters.fromDate) }),
        ...(filters.toDate && { to_date: encodeDateParam(filters.toDate) }),
        ...(filters.eventType && { event_type: filters.eventType }),
        ...(filters.scopeType && { scope_type: filters.scopeType }),
        ...(filters.scopeId && filters.scopeId.length > 0 && { scope_id: filters.scopeId }),
        ...(filters.actorId && filters.actorId.length > 0 && { actor_id: filters.actorId }),
        ...(sortField &&
          sortOrder !== 0 && {
            sort: `${sortField}:${sortOrder === 1 ? 'asc' : 'desc'}`,
          }),
      },
    };
  });
}
